import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, StaggerContainer, StaggerItem } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { BorderCard } from '../components/BorderCard';
import { 
  ShoppingBag, 
  Users, 
  Sparkles, 
  ClipboardList, 
  Layout, 
  Package, 
  GitBranch,
  Heart,
  Shirt,
  Info,
  BookOpen,
  Home
} from 'lucide-react';

const aboutBrand = { 
  id: 'about-brand', 
  title: 'About The Brand', 
  icon: Info, 
  path: '/what/about-brand',
  description: 'Brand Induction Presentation'
};

const categories = [
  { 
    id: 'product', 
    title: 'Product', 
    icon: ShoppingBag, 
    path: '/what/product',
    description: 'Apparel, Accessories & Footwear training'
  },
  { 
    id: 'product-in-detail', 
    title: 'Product In Detail', 
    icon: BookOpen, 
    path: '/what/product-in-detail',
    description: 'Deep dive into fabrics, fits, construction, and key selling points'
  },
  { 
    id: 'customer-service', 
    title: 'Customer Service Training', 
    icon: Users, 
    path: '/what/customer-service',
    description: 'BEARS, PAWS & GUEST frameworks'
  },
  { 
    id: 'crm', 
    title: 'CRM – Customer Relationship Management', 
    icon: Heart, 
    path: '/what/crm',
    description: 'Customer engagement & loyalty'
  },
  { 
    id: 'styling', 
    title: 'Styling', 
    icon: Shirt, 
    path: '/what/styling',
    description: 'Style like a curator, not a seller'
  },
  { 
    id: 'grooming', 
    title: 'Grooming Standards', 
    icon: Sparkles, 
    path: '/what/grooming',
    description: 'Professional appearance guidelines'
  },
  { 
    id: 'sop', 
    title: 'SOP', 
    icon: ClipboardList, 
    path: '/what/sop',
    description: 'Standard operating procedures'
  },
  { 
    id: 'vm-guidelines', 
    title: 'VM Guidelines', 
    icon: Layout, 
    path: '/what/vm-guidelines',
    description: 'Visual merchandising standards'
  },
  { 
    id: 'store-upkeep', 
    title: 'Store Upkeep', 
    icon: Home, 
    path: '/what/store-upkeep',
    description: 'Maintain VM standards, cleanliness, and overall store presentation'
  },
  { 
    id: 'stock-room', 
    title: 'Stock Room Guidelines', 
    icon: Package, 
    path: '/what/stock-room',
    description: 'Inventory & storage management'
  },
  { 
    id: 'escalation', 
    title: 'Escalation Matrix', 
    icon: GitBranch, 
    path: '/what/escalation-matrix',
    description: 'Organizational hierarchy & contacts'
  },
];

const CategoryCard = ({ category, onClick }) => {
  const Icon = category.icon;
  
  return (
    <BorderCard 
      onClick={onClick}
      className="p-8 group"
    >
      <div className="flex items-start gap-4">
        <div className="p-3 border border-[rgba(26,26,26,0.1)] group-hover:border-[#D32F2F] group-hover:bg-[#D32F2F]/5 transition-all duration-500">
          <Icon size={24} strokeWidth={1.5} className="text-[#1A1A1A] group-hover:text-[#D32F2F] transition-colors duration-500" />
        </div>
        <div>
          <h3 className="text-sm uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] mb-2">
            {category.title}
          </h3>
          <p className="text-xs text-[#1A1A1A]/60 leading-relaxed">
            {category.description}
          </p>
        </div>
      </div>
    </BorderCard>
  );
};

const WhatSection = () => {
  const navigate = useNavigate();
  const AboutIcon = aboutBrand.icon;

  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/navigation" label="Back" />

      {/* Single Parent Container for Perfect Alignment */}
      <div className="max-w-6xl mx-auto w-full">
        {/* Header - Centered */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Modules
          </h2>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A]">
            What
          </h1>
        </motion.div>

        {/* About The Brand Card - Centered in 3-Column Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 w-full"
        >
          {/* Empty Left Column */}
          <div className="hidden lg:block" />
          
          {/* Center Column - About The Brand Card */}
          <BorderCard 
            onClick={() => navigate(aboutBrand.path)}
            className="p-8 group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 border border-[rgba(26,26,26,0.1)] group-hover:border-[#D32F2F] group-hover:bg-[#D32F2F]/5 transition-all duration-500">
                <AboutIcon size={24} strokeWidth={1.5} className="text-[#1A1A1A] group-hover:text-[#D32F2F] transition-colors duration-500" />
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] mb-2">
                  {aboutBrand.title}
                </h3>
                <p className="text-xs text-[#1A1A1A]/60 leading-relaxed">
                  {aboutBrand.description}
                </p>
              </div>
            </div>
          </BorderCard>
          
          {/* Empty Right Column */}
          <div className="hidden lg:block" />
        </motion.div>

        {/* Training Modules Grid - Same Container */}
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {categories.map((category) => (
            <StaggerItem key={category.id}>
              <CategoryCard 
                category={category} 
                onClick={() => navigate(category.path)}
              />
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </PageTransition>
  );
};

export default WhatSection;
